/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import { EntityLink } from '@shared/types/responses/streaming';
import { 
  ExternalLink, 
  Plus, 
  Edit, 
  Eye, 
  Trash2,
  Brain,
  Target,
  Map as MapIcon,
  BookOpen,
  GitBranch as RouteIcon,
  Play,
  ClipboardCheck,
  Trophy,
  Network,
  TreePine,
  Bug,
  CheckSquare,
  Sparkles,
  Rocket,
  FileText,
  Layers,
  Folder,
  User,
  Settings,
  DollarSign,
  Calendar,
  Tag,
  Link,
  Database,
  Package,
  BarChart3,
  MessageSquare,
  Bell,
  Search,
  Star,
  type LucideIcon
} from 'lucide-react';
import { cn } from '@shared/lib/utils';

// Icon mapping for iconHint values (custom entity types)
const iconHintMap: Record<string, LucideIcon> = {
  document: FileText,
  folder: Folder,
  user: User,
  settings: Settings,
  money: DollarSign,
  calendar: Calendar,
  tag: Tag,
  link: Link,
  database: Database,
  generic: Package,
  chart: BarChart3,
  message: MessageSquare,
  notification: Bell,
  search: Search,
  star: Star,
};

interface EntityLinksDisplayProps {
  entityLinks: EntityLink[];
  animateNew?: boolean;
  className?: string;
  /** Optional navigation function provided by parent (e.g., from React Router's useNavigate or wouter) */
  onNavigate?: (path: string) => void;
}

// Icon mapping for different entity types
const getEntityIcon = (type: string, subtype?: string, iconHint?: string): LucideIcon => {
  // First check iconHint for custom entity types
  if (iconHint && iconHintMap[iconHint]) {
    return iconHintMap[iconHint];
  }
  
  // Then fall back to built-in type mapping
  switch (type) {
    case 'topic':
      return Brain;
    case 'skill':
      return Target;
    case 'learning_plan':
      return MapIcon;
    case 'learning_module':
      return BookOpen;
    case 'learning_path':
      return RouteIcon;
    case 'learning_activity':
      return Play;
    case 'assessment':
      return ClipboardCheck;
    case 'learning_outcome':
      return Trophy;
    case 'skill_graph':
      return Network;
    case 'knowledge_tree':
      return TreePine;
    case 'issue':
      // Different icons for different issue types
      switch (subtype?.toLowerCase()) {
        case 'bug':
          return Bug;
        case 'task':
          return CheckSquare;
        case 'feature':
          return Sparkles;
        case 'release':
          return Rocket;
        default:
          return CheckSquare;
      }
    case 'user_story':
      return FileText;
    case 'epic':
      return Layers;
    default:
      return ExternalLink;
  }
};

// Color mapping for issue types (different from operation colors)
const getIssueTypeColor = (issueType?: string) => {
  switch (issueType?.toLowerCase()) {
    case 'bug':
      return 'text-red-700 bg-red-50 border-red-300';
    case 'task':
      return 'text-blue-700 bg-blue-50 border-blue-300';
    case 'feature':
      return 'text-purple-700 bg-purple-50 border-purple-300';
    case 'release':
      return 'text-orange-700 bg-orange-50 border-orange-300';
    default:
      return 'text-gray-700 bg-gray-50 border-gray-300';
  }
};

// Color mapping for different operations
const getOperationColor = (operation: EntityLink['operation']) => {
  switch (operation) {
    case 'created':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'updated':
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'referenced':
      return 'text-gray-600 bg-gray-50 border-gray-200';
    case 'deleted':
      return 'text-red-600 bg-red-50 border-red-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

// Icon mapping for different operations
const getOperationIcon = (operation: EntityLink['operation']) => {
  switch (operation) {
    case 'created':
      return Plus;
    case 'updated':
      return Edit;
    case 'referenced':
      return Eye;
    case 'deleted':
      return Trash2;
    default:
      return ExternalLink;
  }
};

// Route mapping for different entity types (matching PlanList.tsx)
const getEntityRoute = (
  entityType: string, 
  entityId: number | string, 
  identifier?: string,
  routePattern?: string
): string | null => {
  // First check if a custom route pattern is provided
  if (routePattern) {
    // Replace :id placeholder with actual ID
    return routePattern.replace(':id', String(entityId));
  }
  
  // Then fall back to built-in route mapping
  switch (entityType.toLowerCase()) {
    case 'topic':
      return `/topics/${entityId}`;
    case 'skill':
      return `/skills/${entityId}`;
    case 'learning_plan':
      return `/learning/plans/${entityId}`;
    case 'learning_module':
      return `/learning/modules/${entityId}`;
    case 'learning_path':
      return `/learning/paths/${entityId}`;
    case 'learning_activity':
      return `/learning/activities/${entityId}`;
    case 'assessment':
      return `/assessments/${entityId}`;
    case 'learning_outcome':
      return `/learning/outcomes/${entityId}`;
    case 'skill_graph':
      return `/skill-graphs/${entityId}`;
    case 'knowledge_tree':
      return `/knowledge-trees/${entityId}`;
    case 'issue':
      // Use identifier (e.g., "BUG-001") as hash anchor for scrolling to issue
      // Extract just the ID part (e.g., "BUG-001" from "BUG-001: Title")
      const issueId = identifier?.split(':')[0].trim();
      return issueId ? `/issues#${issueId}` : `/issues`;
    case 'user_story':
      // Use identifier (e.g., "US-001") as hash anchor for scrolling to story
      const storyId = identifier?.split(':')[0].trim();
      return storyId ? `/user-stories#${storyId}` : `/user-stories`;
    case 'epic':
      // Use identifier (e.g., "ADM-PLT") as hash anchor for scrolling to epic
      const epicId = identifier?.trim();
      return epicId ? `/user-stories#${epicId}` : `/user-stories`;
    default:
      return null;
  }
};

// Display name formatting
const formatEntityType = (type: string, displayName?: string): string => {
  // Use displayName if provided (for custom entity types)
  if (displayName) {
    return displayName;
  }
  // Fall back to formatting the type string
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Scroll to hash element and highlight it
const scrollToHashElement = (hash: string) => {
  setTimeout(() => {
    const element = document.getElementById(hash);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Add a highlight effect to help locate the element
      element.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
      }, 2000);
    }
  }, 100);
};

// Navigate using SPA-style navigation (works with both React Router and wouter)
const navigateToRoute = (route: string, e: React.MouseEvent, onNavigate?: (path: string) => void) => {
  e.preventDefault();
  
  // Parse the route to separate path and hash
  const [path, hash] = route.split('#');
  
  // Use the provided navigation function if available (preferred for React Router/wouter)
  if (onNavigate) {
    onNavigate(path);
  } else {
    // Fallback: Use History API for SPA navigation
    window.history.pushState({}, '', route);
    
    // Dispatch a popstate event to notify routers of the navigation
    window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
  }
  
  // If there's a hash, scroll to the element after a short delay
  // to allow the page to render
  if (hash) {
    // Update the URL hash if we used the callback (which navigates to path only)
    if (onNavigate) {
      window.history.replaceState({}, '', route);
    }
    scrollToHashElement(hash);
  }
};

export function EntityLinksDisplay({ entityLinks, animateNew, className, onNavigate }: EntityLinksDisplayProps) {
  if (!entityLinks || entityLinks.length === 0) {
    return null;
  }

  // Group links by operation type
  const groupedLinks = entityLinks.reduce((acc, link) => {
    if (!acc[link.operation]) {
      acc[link.operation] = [];
    }
    acc[link.operation].push(link);
    return acc;
  }, {} as Record<string, EntityLink[]>);

  return (
    <div className={cn("w-full mt-3 overflow-hidden", className)}>
      <div className="text-xs font-medium text-muted-foreground mb-2">
        Related Entities ({entityLinks.length})
      </div>
      
      <div className="space-y-3">
        {Object.entries(groupedLinks).map(([operation, links]) => {
          const OperationIcon = getOperationIcon(operation as EntityLink['operation']);
          
          return (
            <div key={operation}>
              <div className="flex items-center gap-2 mb-2">
                <OperationIcon className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground capitalize">
                  {operation} ({links.length})
                </span>
              </div>
              
              <div className="grid gap-2 grid-cols-1">
                {links.map((link, index) => {
                  // Get subtype for issues (Bug, Task, Feature, Release)
                  const subtype = link.subtype || link.issueType;
                  
                  // For epics and user stories, the id field IS the identifier
                  // For issues, use the identifier field if present, otherwise extract from id
                  let identifier = link.identifier;
                  if (!identifier && (link.type === 'epic' || link.type === 'user_story')) {
                    identifier = String(link.id);
                  } else if (!identifier && link.type === 'issue') {
                    // Extract issue ID from full ID (e.g., "BUG-001" from "BUG-001: Title")
                    identifier = String(link.id).split(':')[0].trim();
                  }
                  
                  // Get icon - use iconHint for custom types
                  const EntityIcon = getEntityIcon(link.type, subtype, link.iconHint);
                  
                  // Use issue type color for issues, otherwise use operation color
                  const displayColor = link.type === 'issue' 
                    ? getIssueTypeColor(subtype)
                    : getOperationColor(link.operation);
                  
                  // Get route - use routePattern for custom types
                  const route = getEntityRoute(link.type, link.id, identifier, link.routePattern);
                  
                  const linkContent = (
                    <div className={cn(
                      "flex items-center gap-2 p-2 rounded border text-xs transition-all duration-300 min-w-0 max-w-full",
                      displayColor,
                      route ? "hover:opacity-80 cursor-pointer" : "",
                      animateNew && link.operation === 'created' && "animate-pulse border-green-400 bg-green-50 scale-105"
                    )}>
                      <EntityIcon className="h-3 w-3 flex-shrink-0" />
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="font-medium truncate">
                          {identifier && <span className="font-mono mr-1">{identifier}</span>}
                          {link.name}
                        </div>
                        <div className="text-xs opacity-75 truncate">
                          {link.type === 'issue' && subtype && (
                            <span className="font-semibold uppercase mr-1">{subtype}</span>
                          )}
                          {formatEntityType(link.type, link.displayName)}
                          {link.id && ` • ID: ${link.id}`}
                        </div>
                      </div>
                      {route && <ExternalLink className="h-3 w-3 flex-shrink-0" />}
                    </div>
                  );
                  
                  if (route) {
                    return (
                      <a 
                        key={`${link.type}-${link.id}-${index}`} 
                        href={route}
                        onClick={(e) => navigateToRoute(route, e, onNavigate)}
                        className="no-underline"
                      >
                        {linkContent}
                      </a>
                    );
                  }
                  
                  return (
                    <div key={`${link.type}-${link.id}-${index}`}>
                      {linkContent}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
